/**
 * Escribir en el manuscrito.
 *
 * Es la única pantalla del Studio que toca el disco del autor, así que se comporta como tal: lo
 * primero que se ve no es un botón, es QUÉ se va a escribir —las intervenciones exactas, una a
 * una, con lo que quita y lo que pone—. Y el destino, con su promesa: el build de partida no se
 * toca nunca.
 *
 * Si algo falla, lo primero que se lee es que no se ha creado ningún build. El detalle técnico va
 * después: cuando alguien teme por su manuscrito, el orden de las frases importa más que su
 * contenido.
 */
import { useEffect, useState } from "react";
import { vistaPrevia, aplicar, miles } from "../api";
import type { VistaPrevia, Aplicado } from "../api";

const NOMBRE: Record<string, [string, string]> = {
  reemplazar: ["reemplazo", "media"],
  insertar_despues: ["párrafo nuevo", "baja"],
  borrar_parrafo: ["párrafo borrado", "alta"],
};

const fichero = (ruta: string) => ruta.split("/").filter(Boolean).pop() ?? ruta;

export default function Aplicar(
  { carpeta, doc, original, propuesta, autor, opId, alCerrar }: {
    carpeta: string; doc: string; original: string; propuesta: string;
    autor: string; opId: string | null; alCerrar: () => void;
  },
) {
  const [previa, setPrevia] = useState<VistaPrevia | null>(null);
  const [hecho, setHecho] = useState<Aplicado | null>(null);
  const [fallo, setFallo] = useState<{ mensaje: string; detalle?: unknown } | null>(null);
  const [trabajando, setTrabajando] = useState(true);

  useEffect(() => {
    let vivo = true;
    vistaPrevia({ dir: carpeta, docPath: doc, original, propuesta, autor })
      .then((p) => { if (vivo) setPrevia(p); })
      .catch((e) => { if (vivo) setFallo({ mensaje: e.message }); })
      .finally(() => { if (vivo) setTrabajando(false); });
    return () => { vivo = false; };
  }, [carpeta, doc, original, propuesta, autor]);

  async function escribir() {
    setTrabajando(true);
    setFallo(null);
    try {
      setHecho(await aplicar({
        dir: carpeta, docPath: doc, original, propuesta, autor,
        sha256: previa?.sha256, ref: opId,
      }));
    } catch (e) {
      const err = e as Error & { datos?: { sin_ancla?: unknown; verificacion?: unknown } };
      setFallo({ mensaje: err.message, detalle: err.datos?.sin_ancla ?? err.datos?.verificacion });
    } finally {
      setTrabajando(false);
    }
  }

  // ── Ya está escrito ──────────────────────────────────────────────────────────────────────
  if (hecho) {
    return (
      <section className="carta">
        <h2>Hecho<span className="sub">{fichero(hecho.salida)}</span></h2>
        <div className="cifra">
          <b>{hecho.aplicadas}</b><span>cambios escritos con control de cambios</span>
        </div>

        {(() => {
          const malos = hecho.verificacion.checks.filter((c) => !c.ok);
          return (
            <>
              <p className="vacio">
                {hecho.verificacion.checks.length - malos.length} de {hecho.verificacion.checks.length} comprobaciones
                en verde, incluida la que importa: <b>rechazar todo en Word devuelve tu build anterior, palabra por palabra</b>.
              </p>
              {malos.length > 0 && (
                <div className="issues">
                  {malos.map((c, i) => (
                    <div className="issue" key={i}>
                      <span className="grado alta">{c.check}</span>
                      <p className="vacio">{c.detalle || "sin detalle"}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        <ol className="pasos">
          <li>Ábrelo en Word: los cambios aparecen firmados por <b>{hecho.autor}</b>.</li>
          <li>Acepta o rechaza <b>por autor de revisión</b>: es un clic para los {hecho.aplicadas}.</li>
          <li>Guarda el build siguiente y vuelve aquí a medir la pasada.</li>
        </ol>

        <div className="dir">
          <button className="btn fantasma" onClick={alCerrar}>Cerrar</button>
        </div>
      </section>
    );
  }

  // ── Algo ha fallado ──────────────────────────────────────────────────────────────────────
  if (fallo) {
    return (
      <section className="carta">
        <h2>No se ha escrito nada<span className="sub">tu manuscrito sigue intacto</span></h2>
        <p><b>{fichero(doc)} no se ha tocado, y no se ha creado ningún build.</b></p>
        <p className="vacio">{fallo.mensaje}</p>
        {Array.isArray(fallo.detalle) && (
          <div className="issues">
            {(fallo.detalle as { motivo: string; texto: string }[]).slice(0, 8).map((d, i) => (
              <div className="issue" key={i}>
                <span className="grado alta">sin sitio</span>
                <p className="vacio">{d.motivo}</p>
                <blockquote>{d.texto}</blockquote>
              </div>
            ))}
          </div>
        )}
        <div className="dir">
          <button className="btn fantasma" onClick={alCerrar}>Cerrar</button>
        </div>
      </section>
    );
  }

  // ── Antes de escribir ────────────────────────────────────────────────────────────────────
  if (!previa) {
    return (
      <section className="carta">
        <h2>Aplicar al manuscrito<span className="sub">mirando el documento…</span></h2>
        <p className="vacio">Buscando dónde encaja cada cambio en {fichero(doc)}.</p>
      </section>
    );
  }

  const r = previa.resumen;
  return (
    <section className="carta">
      <h2>Aplicar al manuscrito
        <span className="sub">{previa.lote.length} {previa.lote.length === 1 ? "intervención" : "intervenciones"}</span>
      </h2>

      <div className="rejilla">
        <div className="cifra"><b>{r.reemplazos}</b><span>reemplazos</span></div>
        <div className="cifra"><b>{r.inserciones}</b><span>párrafos nuevos</span></div>
        <div className="cifra"><b>{r.palabras_delta > 0 ? "+" : ""}{miles(r.palabras_delta)}</b><span>palabras</span></div>
      </div>

      <div className="dir">
        <span className="mono">{fichero(previa.origen)} → {fichero(previa.salida)}</span>
      </div>
      <p className="vacio">Tu {fichero(previa.origen)} no se toca. Nunca.</p>

      {previa.duplicados.length > 0 && (
        <p className="vacio" style={{ color: "var(--acc)" }}>
          Ojo: hay dos ficheros con el número {previa.duplicados.join(", ")} en esa carpeta.
          Revisa cuál es el bueno antes de seguir.
        </p>
      )}

      {previa.sin_ancla.length > 0 && (
        <div className="issues">
          <div className="issue">
            <span className="grado alta">no se aplicará</span>
            <p>{previa.sin_ancla.length} {previa.sin_ancla.length === 1 ? "cambio no encuentra" : "cambios no encuentran"} su
               sitio en el documento.</p>
            <p className="vacio">{previa.sin_ancla[0].motivo}</p>
          </div>
        </div>
      )}

      <div className="issues">
        {previa.lote.slice(0, 20).map((x, i) => (
          <div className="issue" key={i}>
            <span className={"grado " + (NOMBRE[x.op]?.[1] ?? "media")}>{NOMBRE[x.op]?.[0] ?? x.op}</span>
            <p className="vacio">párrafo {x.parrafo}</p>
            {x.op === "reemplazar" && (
              <blockquote>
                <del>{x.buscar}</del> <ins>{x.reemplazar}</ins>
              </blockquote>
            )}
            {x.op === "insertar_despues" && <blockquote><ins>{(x.texto ?? []).join(" / ")}</ins></blockquote>}
            {x.op === "borrar_parrafo" && <blockquote><del>{x.huella}</del></blockquote>}
          </div>
        ))}
        {previa.lote.length > 20 && (
          <p className="vacio">… y {previa.lote.length - 20} intervenciones más.</p>
        )}
      </div>

      <div className="dir">
        <button className="btn" onClick={escribir} disabled={trabajando || !previa.lote.length}>
          {trabajando ? "Escribiendo…" : `Aplicar y crear ${fichero(previa.salida)}`}
        </button>
        <button className="btn fantasma" onClick={alCerrar}>Descartar</button>
      </div>
    </section>
  );
}
